/**
 * Per-platform previews — the composer's wow moment.
 *
 * Each card mirrors the structure of the real product (Facebook page post,
 * Instagram tile, X post, LinkedIn update, WordPress article header): the same
 * header rhythm, the same media crop, the same action bar, the same truncation
 * point, and the platform's own link colour. Surfaces stay on our neutral scale
 * so the previews look right in both themes.
 */

import { useState } from 'react';
import type { ReactNode } from 'react';
import {
  Bookmark,
  Clapperboard,
  Ellipsis,
  Globe,
  Heart,
  ImageOff,
  MessageCircle,
  Repeat2,
  Send,
  Share2,
  ThumbsUp,
} from 'lucide-react';
import { PLATFORM_SPECS } from '../../../shared/platforms';
import type { MediaAsset, PlatformId, PlatformPostMeta } from '../../../shared/types';
import { readingMinutes, stripHtml } from '../../../shared/utils';
import { Avatar, PlatformGlyph, Skeleton } from '../ui';
import { cn } from '../../lib/utils';
import { assetUrl } from './composerUtils';
import { entityTextWithOverflow, linkStyle, renderArticleBody } from './previewText';

export interface PlatformPreviewProps {
  platform: PlatformId;
  content: string;
  meta?: PlatformPostMeta;
  media?: MediaAsset[];
  /** Connected account name, or null when nothing is connected yet. */
  accountName?: string | null;
  accountHandle?: string | null;
  avatarUrl?: string | null;
  /** "Just now", "Scheduled for Mar 4, 2:30 PM" … */
  timestamp?: string;
  className?: string;
}

/* ------------------------------------------------------------------ */
/* Shared pieces                                                       */
/* ------------------------------------------------------------------ */

function imagesOnly(media: MediaAsset[]): MediaAsset[] {
  return media.filter((asset) => asset.kind === 'image');
}

function MediaTile({ asset, className }: { asset: MediaAsset; className?: string }) {
  if (asset.kind === 'video') {
    return (
      <div
        className={cn(
          'flex size-full min-h-24 items-center justify-center gap-2 bg-sunken text-subtle',
          className,
        )}
      >
        <Clapperboard className="size-5" aria-hidden />
        <span className="text-xs">Video</span>
      </div>
    );
  }
  return (
    <img
      src={assetUrl(asset)}
      alt={asset.altText ?? asset.filename}
      loading="lazy"
      className={cn('size-full object-cover', className)}
    />
  );
}

/**
 * The album layouts the social platforms use: one full-bleed, two side by side,
 * three as a hero plus a stacked pair, four or more as a 2×2 with a "+N" tile.
 */
function Mosaic({
  media,
  ratio,
  className,
}: {
  media: MediaAsset[];
  /** aspect-ratio for the single-image case. */
  ratio: string;
  className?: string;
}) {
  if (!media.length) return null;
  const [first, second, third, fourth] = media;

  if (media.length === 1 && first) {
    return (
      <div className={cn('overflow-hidden bg-sunken', className)} style={{ aspectRatio: ratio }}>
        <MediaTile asset={first} />
      </div>
    );
  }

  if (media.length === 2 && first && second) {
    return (
      <div className={cn('grid grid-cols-2 gap-0.5 overflow-hidden bg-sunken', className)}>
        {[first, second].map((asset) => (
          <div key={asset.id} className="aspect-square overflow-hidden">
            <MediaTile asset={asset} />
          </div>
        ))}
      </div>
    );
  }

  if (media.length === 3 && first && second && third) {
    return (
      <div className={cn('grid grid-cols-2 gap-0.5 overflow-hidden bg-sunken', className)}>
        <div className="row-span-2 overflow-hidden">
          <MediaTile asset={first} className="h-full" />
        </div>
        <div className="aspect-[16/9] overflow-hidden">
          <MediaTile asset={second} />
        </div>
        <div className="aspect-[16/9] overflow-hidden">
          <MediaTile asset={third} />
        </div>
      </div>
    );
  }

  const rest = media.length - 4;
  return (
    <div className={cn('grid grid-cols-2 gap-0.5 overflow-hidden bg-sunken', className)}>
      {[first, second, third, fourth].map((asset, index) =>
        asset ? (
          <div key={asset.id} className="relative aspect-square overflow-hidden">
            <MediaTile asset={asset} />
            {index === 3 && rest > 0 && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/55 text-lg font-semibold text-white">
                +{rest}
              </div>
            )}
          </div>
        ) : null,
      )}
    </div>
  );
}

function MissingImage({ platform, hint }: { platform: PlatformId; hint: string }) {
  return (
    <div className="flex aspect-square flex-col items-center justify-center gap-2 border-y border-dashed border-border bg-sunken/60 px-6 text-center">
      <span className="flex size-9 items-center justify-center rounded-full bg-elevated text-subtle">
        <ImageOff className="size-4" aria-hidden />
      </span>
      <p className="text-xs font-medium text-muted">
        {PLATFORM_SPECS[platform].shortName} posts need an image
      </p>
      <p className="max-w-[16rem] text-[11px] leading-relaxed text-subtle">{hint}</p>
    </div>
  );
}

/** Caption that collapses at the platform's own "see more" point. */
function ClampedCaption({
  text,
  platform,
  limit,
  moreLabel,
  className,
  shortenUrls,
  leading,
  emptyLabel = 'Your caption will appear here.',
}: {
  text: string;
  platform: PlatformId;
  limit: number;
  moreLabel: string;
  className?: string;
  shortenUrls?: boolean;
  /** Rendered before the caption — Instagram's bold username, for example. */
  leading?: ReactNode;
  emptyLabel?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const trimmed = text.trimEnd();

  if (!trimmed) {
    return <p className={cn('text-subtle', className)}>{emptyLabel}</p>;
  }

  const overLimit = trimmed.length > limit + 24;
  const shown = overLimit && !expanded ? `${trimmed.slice(0, limit).trimEnd()}… ` : trimmed;

  return (
    <p className={cn('whitespace-pre-wrap break-words', className)} style={linkStyle(platform)}>
      {leading}
      {entityTextWithOverflow(shown, platform, { ...(shortenUrls ? { shortenUrls } : {}) })}
      {overLimit && !expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="font-medium text-subtle transition-colors hover:text-muted"
        >
          {moreLabel}
        </button>
      )}
    </p>
  );
}

function ActionBar({
  items,
  className,
}: {
  items: Array<{ icon: ReactNode; label: string }>;
  className?: string;
}) {
  return (
    <div
      className={cn('flex items-center justify-around px-2 py-1.5 text-muted', className)}
      aria-hidden
    >
      {items.map((item) => (
        <span
          key={item.label}
          className="flex items-center gap-1.5 rounded px-2 py-1 text-[13px] font-medium"
        >
          {item.icon}
          <span className="hidden sm:inline">{item.label}</span>
        </span>
      ))}
    </div>
  );
}

function accountLabel(name: string | null | undefined, platform: PlatformId): string {
  return name?.trim() || `Your ${PLATFORM_SPECS[platform].shortName} account`;
}

function handleFor(handle: string | null | undefined, name: string | null | undefined): string {
  const source = handle?.replace(/^@/, '') || name || 'yourbrand';
  return `@${source.toLowerCase().replace(/[^a-z0-9_]+/g, '').slice(0, 15) || 'yourbrand'}`;
}

const CARD = 'overflow-hidden rounded-xl border border-border bg-surface';

/* ------------------------------------------------------------------ */
/* Facebook                                                            */
/* ------------------------------------------------------------------ */

function FacebookPreview({
  content,
  media = [],
  accountName,
  avatarUrl,
  timestamp,
  className,
}: PlatformPreviewProps) {
  const images = media;
  return (
    <article className={cn(CARD, className)}>
      <header className="flex items-center gap-2.5 px-4 pb-2.5 pt-3.5">
        <Avatar
          name={accountLabel(accountName, 'facebook')}
          src={avatarUrl ?? null}
          size="md"
          className="ring-0"
        />
        <div className="min-w-0 flex-1 leading-tight">
          <p className="truncate text-[15px] font-semibold text-fg">
            {accountLabel(accountName, 'facebook')}
          </p>
          <p className="flex items-center gap-1 text-xs text-subtle">
            <span>{timestamp ?? 'Just now'}</span>
            <span aria-hidden>·</span>
            <Globe className="size-3" aria-hidden />
          </p>
        </div>
        <Ellipsis className="size-4 shrink-0 text-subtle" aria-hidden />
      </header>

      <ClampedCaption
        text={content}
        platform="facebook"
        limit={420}
        moreLabel="See more"
        className="px-4 pb-3 text-[15px] leading-6 text-fg"
      />

      {images.length > 0 && <Mosaic media={images} ratio="1.91 / 1" className="border-y border-border" />}

      <ActionBar
        className="border-t border-border"
        items={[
          { icon: <ThumbsUp className="size-4" />, label: 'Like' },
          { icon: <MessageCircle className="size-4" />, label: 'Comment' },
          { icon: <Share2 className="size-4" />, label: 'Share' },
        ]}
      />
    </article>
  );
}

/* ------------------------------------------------------------------ */
/* Instagram                                                           */
/* ------------------------------------------------------------------ */

function InstagramPreview({
  content,
  meta,
  media = [],
  accountName,
  accountHandle,
  avatarUrl,
  className,
}: PlatformPreviewProps) {
  const images = imagesOnly(media);
  const first = images[0];
  const username = handleFor(accountHandle, accountName).slice(1);

  return (
    <article className={cn(CARD, 'max-w-md', className)}>
      <header className="flex items-center gap-2.5 px-3 py-2.5">
        <span
          className="rounded-full p-[2px]"
          style={{ backgroundImage: 'linear-gradient(45deg,#F58529,#DD2A7B,#8134AF)' }}
          aria-hidden
        >
          <span className="block rounded-full bg-surface p-[2px]">
            <Avatar name={username} src={avatarUrl ?? null} size="sm" className="ring-0" />
          </span>
        </span>
        <p className="min-w-0 flex-1 truncate text-[13px] font-semibold text-fg">{username}</p>
        <Ellipsis className="size-4 shrink-0 text-subtle" aria-hidden />
      </header>

      {first ? (
        <div className="relative aspect-square overflow-hidden bg-sunken">
          <MediaTile asset={first} />
          {images.length > 1 && (
            <>
              <span className="absolute right-3 top-3 rounded-full bg-black/55 px-2 py-0.5 text-[11px] font-medium text-white backdrop-blur-sm">
                1/{images.length}
              </span>
              <span className="absolute inset-x-0 bottom-3 flex items-center justify-center gap-1">
                {images.slice(0, 8).map((asset, index) => (
                  <span
                    key={asset.id}
                    className={cn(
                      'size-1.5 rounded-full',
                      index === 0 ? 'bg-white' : 'bg-white/45',
                    )}
                  />
                ))}
              </span>
            </>
          )}
        </div>
      ) : (
        <MissingImage
          platform="instagram"
          hint="Attach an image on the left, pick one for this variant, or generate one with AI."
        />
      )}

      <div className="flex items-center gap-4 px-3 pb-1 pt-2.5 text-fg" aria-hidden>
        <Heart className="size-[22px]" />
        <MessageCircle className="size-[22px]" />
        <Send className="size-[22px]" />
        <Bookmark className="ml-auto size-[22px]" />
      </div>

      <div className="px-3 pb-3.5">
        <ClampedCaption
          text={content}
          platform="instagram"
          limit={125}
          moreLabel="more"
          className="text-[13px] leading-[1.45] text-fg"
          leading={<span className="mr-1.5 font-semibold">{username}</span>}
          emptyLabel="Your caption will appear here."
        />
        {meta?.firstComment?.trim() && (
          <p className="mt-2 border-t border-border pt-2 text-[13px] leading-[1.45] text-muted">
            <span className="font-semibold text-fg">{username}</span>{' '}
            <span style={linkStyle('instagram')}>
              {entityTextWithOverflow(meta.firstComment, 'instagram')}
            </span>
          </p>
        )}
      </div>
    </article>
  );
}

/* ------------------------------------------------------------------ */
/* X                                                                   */
/* ------------------------------------------------------------------ */

function XPost({
  content,
  media,
  accountName,
  accountHandle,
  avatarUrl,
  timestamp,
  connector,
}: {
  content: string;
  media: MediaAsset[];
  accountName?: string | null;
  accountHandle?: string | null;
  avatarUrl?: string | null;
  timestamp?: string;
  /** Draws the thread line under the avatar. */
  connector?: boolean;
}) {
  return (
    <div className="flex gap-3 px-4 py-3">
      <div className="flex shrink-0 flex-col items-center">
        <Avatar name={accountLabel(accountName, 'x')} src={avatarUrl ?? null} size="md" className="ring-0" />
        {connector && <span className="mt-1 w-0.5 flex-1 rounded-full bg-border" aria-hidden />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1 text-[15px] leading-5">
          <span className="truncate font-bold text-fg">{accountLabel(accountName, 'x')}</span>
          <span className="truncate text-subtle">{handleFor(accountHandle, accountName)}</span>
          <span className="text-subtle" aria-hidden>
            ·
          </span>
          <span className="shrink-0 text-subtle">{timestamp ?? 'now'}</span>
          <Ellipsis className="ml-auto size-4 shrink-0 text-subtle" aria-hidden />
        </p>

        <ClampedCaption
          text={content}
          platform="x"
          limit={100_000}
          moreLabel="Show more"
          shortenUrls
          className="mt-0.5 text-[15px] leading-5 text-fg"
          emptyLabel="What's happening?"
        />

        {media.length > 0 && (
          <Mosaic media={media} ratio="16 / 9" className="mt-3 rounded-2xl border border-border" />
        )}

        <div className="mt-3 flex max-w-md items-center justify-between text-subtle" aria-hidden>
          <MessageCircle className="size-[18px]" />
          <Repeat2 className="size-[18px]" />
          <Heart className="size-[18px]" />
          <Share2 className="size-[18px]" />
        </div>
      </div>
    </div>
  );
}

function XPreview({
  content,
  meta,
  media = [],
  accountName,
  accountHandle,
  avatarUrl,
  timestamp,
  className,
}: PlatformPreviewProps) {
  const thread = (meta?.replyThread ?? []).filter((entry) => entry.trim());
  return (
    <article className={cn(CARD, 'max-w-xl divide-y divide-border', className)}>
      <XPost
        content={content}
        media={media}
        accountName={accountName ?? null}
        accountHandle={accountHandle ?? null}
        avatarUrl={avatarUrl ?? null}
        {...(timestamp ? { timestamp } : {})}
        connector={thread.length > 0}
      />
      {thread.map((entry, index) => (
        <XPost
          key={`thread-${index}`}
          content={entry}
          media={[]}
          accountName={accountName ?? null}
          accountHandle={accountHandle ?? null}
          avatarUrl={avatarUrl ?? null}
          timestamp="now"
          connector={index < thread.length - 1}
        />
      ))}
    </article>
  );
}

/* ------------------------------------------------------------------ */
/* LinkedIn                                                            */
/* ------------------------------------------------------------------ */

function LinkedInPreview({
  content,
  media = [],
  accountName,
  avatarUrl,
  timestamp,
  className,
}: PlatformPreviewProps) {
  return (
    <article className={cn(CARD, 'max-w-xl', className)}>
      <header className="flex items-start gap-2.5 px-4 pb-2 pt-3.5">
        <Avatar
          name={accountLabel(accountName, 'linkedin')}
          src={avatarUrl ?? null}
          size="md"
          className="ring-0"
        />
        <div className="min-w-0 flex-1 leading-tight">
          <p className="truncate text-sm font-semibold text-fg">
            {accountLabel(accountName, 'linkedin')}
          </p>
          <p className="truncate text-xs text-subtle">Posting to your LinkedIn feed</p>
          <p className="flex items-center gap-1 text-xs text-subtle">
            <span>{timestamp ?? 'Just now'}</span>
            <span aria-hidden>·</span>
            <Globe className="size-3" aria-hidden />
          </p>
        </div>
        <Ellipsis className="size-4 shrink-0 text-subtle" aria-hidden />
      </header>

      <ClampedCaption
        text={content}
        platform="linkedin"
        limit={210}
        moreLabel="…see more"
        className="px-4 pb-3 text-sm leading-6 text-fg"
      />

      {media.length > 0 && <Mosaic media={media} ratio="1.91 / 1" className="border-y border-border" />}

      <ActionBar
        className="border-t border-border"
        items={[
          { icon: <ThumbsUp className="size-4" />, label: 'Like' },
          { icon: <MessageCircle className="size-4" />, label: 'Comment' },
          { icon: <Repeat2 className="size-4" />, label: 'Repost' },
          { icon: <Send className="size-4" />, label: 'Send' },
        ]}
      />
    </article>
  );
}

/* ------------------------------------------------------------------ */
/* WordPress                                                           */
/* ------------------------------------------------------------------ */

function WordPressPreview({
  content,
  meta,
  media = [],
  accountName,
  timestamp,
  className,
}: PlatformPreviewProps) {
  const featured = imagesOnly(media)[0];
  const title = meta?.title?.trim();
  const categories = meta?.categories ?? [];
  const minutes = readingMinutes(stripHtml(content));

  return (
    <article className={cn(CARD, 'max-w-2xl', className)}>
      {featured ? (
        <div className="aspect-[16/9] overflow-hidden bg-sunken">
          <MediaTile asset={featured} />
        </div>
      ) : (
        <div className="flex h-1.5 w-full" aria-hidden>
          <span
            className="h-full w-full bg-[var(--pap-link)] dark:bg-[var(--pap-link-dark)]"
            style={linkStyle('wordpress')}
          />
        </div>
      )}

      <div className="px-6 pb-6 pt-5">
        <div className="flex flex-wrap items-center gap-2 pb-2 text-[11px] font-semibold uppercase tracking-wide">
          <span style={linkStyle('wordpress')} className="text-[var(--pap-link)] dark:text-[var(--pap-link-dark)]">
            {categories[0] ?? 'Blog'}
          </span>
        </div>

        <h1 className="text-2xl font-semibold leading-snug tracking-tight text-fg">
          {title || <span className="text-subtle">Untitled draft</span>}
        </h1>

        <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-subtle">
          <span>By {accountLabel(accountName, 'wordpress')}</span>
          <span aria-hidden>·</span>
          <span>{timestamp ?? 'Draft'}</span>
          <span aria-hidden>·</span>
          <span>{minutes} min read</span>
        </p>

        {meta?.excerpt?.trim() && (
          <p className="mt-4 border-l-2 border-border pl-4 text-[15px] leading-7 text-muted">
            {meta.excerpt}
          </p>
        )}

        <div className="mt-5 text-fg">
          {content.trim() ? (
            renderArticleBody(content)
          ) : (
            <p className="text-sm text-subtle">The article body will appear here.</p>
          )}
        </div>

        {(meta?.tags?.length ?? 0) > 0 && (
          <div className="mt-5 flex flex-wrap gap-1.5 border-t border-border pt-4">
            {(meta?.tags ?? []).map((tag) => (
              <span
                key={tag}
                className="rounded-full border border-border px-2 py-0.5 text-[11px] text-muted"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}

/* ------------------------------------------------------------------ */
/* Coming soon (TikTok / YouTube)                                      */
/* ------------------------------------------------------------------ */

function ComingSoonPreview({ platform, content, className }: PlatformPreviewProps) {
  const spec = PLATFORM_SPECS[platform];
  return (
    <article className={cn(CARD, 'max-w-md', className)}>
      <div className="flex items-center gap-3 border-b border-border px-4 py-3">
        <span className="flex size-8 items-center justify-center rounded-lg bg-elevated text-muted">
          <PlatformGlyph platform={platform} className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-fg">{spec.name}</p>
          <p className="text-xs text-subtle">Publishing lands with the video pipeline.</p>
        </div>
      </div>
      <p className="whitespace-pre-wrap break-words px-4 py-4 text-sm leading-relaxed text-muted">
        {content.trim() || 'Draft your copy now — it will be ready when the connector ships.'}
      </p>
    </article>
  );
}

/* ------------------------------------------------------------------ */
/* Dispatcher                                                          */
/* ------------------------------------------------------------------ */

/** Renders the preview that matches `platform`. */
export function PlatformPreview(props: PlatformPreviewProps) {
  switch (props.platform) {
    case 'facebook':
      return <FacebookPreview {...props} />;
    case 'instagram':
      return <InstagramPreview {...props} />;
    case 'x':
      return <XPreview {...props} />;
    case 'linkedin':
      return <LinkedInPreview {...props} />;
    case 'wordpress':
      return <WordPressPreview {...props} />;
    default:
      return <ComingSoonPreview {...props} />;
  }
}

/* ------------------------------------------------------------------ */
/* Loading state                                                       */
/* ------------------------------------------------------------------ */

/** Shimmer that keeps the shape of the preview it is standing in for. */
export function PlatformPreviewSkeleton({
  platform,
  className,
}: {
  platform: PlatformId;
  className?: string;
}) {
  const square = platform === 'instagram';
  const article = platform === 'wordpress';

  return (
    <div className={cn(CARD, 'max-w-xl', className)} role="status" aria-label="Writing a draft">
      <div className="flex items-center gap-3 px-4 py-3.5">
        <Skeleton className="size-10 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-3.5 w-32" />
          <Skeleton className="h-2.5 w-20" />
        </div>
        <span className="opacity-70">
          <PlatformGlyph platform={platform} className="size-4 text-subtle" />
        </span>
      </div>

      <div className="space-y-2.5 px-4 pb-4">
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-[92%]" />
        <Skeleton className="h-3 w-[74%]" />
        {article && (
          <>
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-[86%]" />
          </>
        )}
      </div>

      <Skeleton
        className={cn('w-full rounded-none border-y border-border', square ? 'aspect-square' : 'aspect-[16/9]')}
      />

      <div className="flex items-center gap-6 px-4 py-3">
        <Skeleton className="h-3 w-14" />
        <Skeleton className="h-3 w-14" />
        <Skeleton className="h-3 w-14" />
      </div>
    </div>
  );
}
